// The Team members table (WEB_PLAN.md §5.4) as pure functions: the `users` profiles the viewer can see, joined with
// `team_roster` (who has an account, who is invited), presence from the day logs and this week's numbers, then
// searched, sorted and grouped by team the way the iPhone's roster groups them (`store.teamRoster`,
// GroundTeam/Views/Team/Team+Store.swift:97). Account wording follows TeamMembersView / TeamInvitationRow
// (GroundTeam/Views/Sync/TeamMembersView.swift). No React here, so it is unit-tested in node.

import type { MetricsIndex } from '@/domain/metrics';
import { teamMetrics } from '@/domain/metrics';
import type { RecordPayload, TeamPayload, UserPayload } from '@/contracts/models';
import type { Role, Roster, RosterMember, TeamInvitation } from '@/contracts/wire';
import { parseWireDate } from '@/contracts/wireDate';
import { startOfDay, startOfWeek } from '@/domain/calendar';
import { presenceOf, teamRoster, type Presence, type RepPosition } from '@/domain/team';
import { byId, compareLocalized, fullName, idKey, lookup, sameId } from '@/domain/util';

// MARK: - Roster

/** Accepts whatever `team_roster` sent: anything that is not a well-formed entry is left out, never thrown on. */
export function normalizeRoster(json: unknown): Roster {
  const obj = isRecord(json) ? json : {};
  const members = Array.isArray(obj.members) ? obj.members.filter(isRosterMember) : [];
  const invitations = Array.isArray(obj.invitations) ? obj.invitations.filter(isInvitation) : [];
  return { members, invitations };
}

/** One `invitation_json` (invite_member's answer); null when the shape is not one. */
export function normalizeInvitation(json: unknown): TeamInvitation | null {
  return isInvitation(json) ? json : null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isRosterMember(v: unknown): v is RosterMember {
  return isRecord(v) && typeof v.user_id === 'string' && typeof v.profile_id === 'string' && typeof v.role === 'string';
}

function isInvitation(v: unknown): v is TeamInvitation {
  return isRecord(v) && typeof v.id === 'string' && typeof v.profile_id === 'string' && typeof v.role === 'string';
}

// MARK: - Account status

export type AccountStatus = 'signedUp' | 'invited' | 'expired' | 'suspended' | 'none';

export const ACCOUNT_STATUS_TITLES: Readonly<Record<AccountStatus, string>> = {
  signedUp: 'Signed up',
  invited: 'Invited',
  expired: 'Invitation expired',
  suspended: 'Suspended',
  none: 'No account',
};

/** Sort order of the Account column (people with access first). */
const ACCOUNT_ORDER: Readonly<Record<AccountStatus, number>> = { signedUp: 0, invited: 1, expired: 2, suspended: 3, none: 4 };

/** What the roster says about one profile. */
export interface ProfileAccount {
  member?: RosterMember;
  /** Pending invitations for the profile, newest first (the server keeps one per profile, but be tolerant). */
  invitations: TeamInvitation[];
}

export interface AccountInfo {
  status: AccountStatus;
  member?: RosterMember;
  /** The invitation the row acts on: the newest open one, else the newest expired one. */
  invitation?: TeamInvitation;
}

/** `TeamInvitationRow.isExpired`: past `expires_at` (an invitation without one never expires). */
export function isInvitationExpired(invitation: Pick<TeamInvitation, 'expires_at'>, now: Date = new Date()): boolean {
  const expires = parseWireDate(invitation.expires_at);
  return expires !== undefined && expires.getTime() < now.getTime();
}

function createdMs(invitation: TeamInvitation): number {
  return parseWireDate(invitation.created_at)?.getTime() ?? 0;
}

/** Roster entries by lower-case profile id. */
export function indexRoster(roster: Roster): Map<string, ProfileAccount> {
  const out = new Map<string, ProfileAccount>();
  const entry = (profileId: string) => {
    const key = idKey(profileId);
    let e = out.get(key);
    if (!e) {
      e = { invitations: [] };
      out.set(key, e);
    }
    return e;
  };
  for (const m of roster.members) {
    const e = entry(m.profile_id);
    // Two accounts on one profile cannot happen (the server refuses it); keep the active one if it ever did.
    if (!e.member || (e.member.status !== 'active' && m.status === 'active')) e.member = m;
  }
  for (const inv of roster.invitations) entry(inv.profile_id).invitations.push(inv);
  for (const e of out.values()) e.invitations.sort((a, b) => createdMs(b) - createdMs(a));
  return out;
}

/**
 * The Account column: an account on the profile wins (Signed up, or Suspended when the membership is paused), then a
 * pending invitation (Invited, or Invitation expired once every one is past its date), else No account.
 */
export function accountOf(entry: ProfileAccount | undefined, now: Date = new Date()): AccountInfo {
  if (entry?.member) {
    return { status: entry.member.status === 'active' ? 'signedUp' : 'suspended', member: entry.member };
  }
  const invitations = entry?.invitations ?? [];
  const open = invitations.find((i) => !isInvitationExpired(i, now));
  if (open) return { status: 'invited', invitation: open };
  if (invitations.length > 0) return { status: 'expired', invitation: invitations[0] };
  return { status: 'none' };
}

// MARK: - Numbers per person

export interface MemberStats {
  /** Completed visits since the start of this week (lower-case profile id). */
  weekVisits: ReadonlyMap<string, number>;
  /** Today's completed visits and contacts (the iOS roster's summary strip). */
  todayVisits: ReadonlyMap<string, number>;
  todayContacts: ReadonlyMap<string, number>;
  /** Non-archived stores each person owns (`RepDaySummary.ownedRecords`). */
  owned: ReadonlyMap<string, number>;
}

/**
 * Everyone's numbers in two passes over the activities (`teamMetrics`, the same rules as `metricValue`) and one over
 * the stores, instead of a `repSummary` per person.
 */
export function memberStats(
  index: MetricsIndex,
  records: readonly Pick<RecordPayload, 'ownerId' | 'isArchived'>[],
  now: Date,
  weekStartsOn: number,
): MemberStats {
  const week = teamMetrics(index, startOfWeek(now, weekStartsOn), now);
  const today = teamMetrics(index, startOfDay(now), now);
  const owned = new Map<string, number>();
  for (const r of records) {
    if (r.isArchived || !r.ownerId) continue;
    const key = idKey(r.ownerId);
    owned.set(key, (owned.get(key) ?? 0) + 1);
  }
  return {
    weekVisits: new Map(week.map((r) => [r.profile_id, r.visits])),
    todayVisits: new Map(today.map((r) => [r.profile_id, r.visits])),
    todayContacts: new Map(today.map((r) => [r.profile_id, r.contacts])),
    owned,
  };
}

function count(map: ReadonlyMap<string, number>, id: string | null): number {
  return id ? (map.get(idKey(id)) ?? 0) : 0;
}

// MARK: - Rows

/** One person in the table: a profile, or (admins only) an account or invitation whose profile is not synced. */
export interface PersonRow {
  kind: 'person';
  /** Row key: the profile id, or `member:<user_id>` / `invitation:<id>` for a row without a profile item. */
  id: string;
  /** The `users` item (null for a row without one). */
  profileId: string | null;
  user: UserPayload | null;
  /** Full name; the account's email (or "Invite code") when the profile has no name or is missing. */
  name: string;
  email: string;
  title: string;
  team?: TeamPayload;
  teamName: string;
  role: Role;
  isActive: boolean;
  isMe: boolean;
  /**
   * The profile was written by a newer app and only decoded best-effort: shown (read-only, with a badge) so nobody
   * goes missing from Team, but never offered for an invitation, which would build an edit from it.
   */
  newerApp: boolean;
  /** Null while the roster is unknown (loading, failed, or a rep's session). */
  account: AccountInfo | null;
  lastSeenAt: Date | null;
  presence: Presence;
  storesOwned: number;
  weekVisits: number;
}

/** A team heading inside the table. */
export interface GroupRow {
  kind: 'group';
  id: string;
  title: string;
  color?: string;
  /** "· Jane Doe" after the team name (the team's manager), as on the iPhone. */
  managerName?: string;
  count: number;
}

export type MemberTableRow = PersonRow | GroupRow;

export interface PeopleInput {
  /** The viewer's visible people (`visibleUsers`), undecodable ones included (best-effort values). */
  users: readonly UserPayload[];
  /** Lower-case ids of the `users` written by a newer app (`DataItem.undecodable`). */
  newerAppIds?: ReadonlySet<string>;
  teams: readonly TeamPayload[];
  /** Null while unknown: the Account column then shows a placeholder. */
  roster: Roster | null;
  /**
   * Add rows for roster entries whose profile is not among `users` (an account or invitation for a deleted or
   * not-yet-synced profile). For admins, who see every profile; a manager's roster covers people they cannot see.
   */
  includeOrphans: boolean;
  meId: string | null;
  positions: ReadonlyMap<string, Pick<RepPosition, 'at'>>;
  stats: Pick<MemberStats, 'owned' | 'weekVisits'>;
  now: Date;
}

/** Every row the table can show, before search, sorting and grouping. */
export function peopleRows(input: PeopleInput): PersonRow[] {
  const { users, teams, roster, now } = input;
  const teamsById = byId(teams);
  const index = roster ? indexRoster(roster) : null;
  const rows: PersonRow[] = users.map((u) => {
    const team = lookup(teamsById, u.teamId);
    const at = input.positions.get(idKey(u.id))?.at ?? null;
    const entry = index?.get(idKey(u.id));
    const name = fullName(u);
    return {
      kind: 'person',
      id: u.id,
      profileId: u.id,
      user: u,
      name: name !== '' ? name : (entry?.member?.email ?? u.email ?? ''),
      email: u.email !== '' ? u.email : (entry?.member?.email ?? ''),
      title: u.title,
      team,
      teamName: team?.name ?? '',
      role: u.role,
      isActive: u.isActive,
      isMe: sameId(u.id, input.meId),
      newerApp: input.newerAppIds?.has(idKey(u.id)) ?? false,
      account: index ? accountOf(entry, now) : null,
      lastSeenAt: at,
      presence: presenceOf(at, now),
      storesOwned: count(input.stats.owned, u.id),
      weekVisits: count(input.stats.weekVisits, u.id),
    };
  });
  if (!index || !input.includeOrphans) return rows;

  const known = new Set(users.map((u) => idKey(u.id)));
  for (const [key, entry] of index) {
    if (known.has(key)) continue;
    const account = accountOf(entry, now);
    const member = entry.member;
    const invitation = account.invitation ?? entry.invitations[0];
    const email = member?.email ?? invitation?.email ?? '';
    const role = member?.role ?? invitation?.role ?? 'rep';
    rows.push({
      kind: 'person',
      id: member ? `member:${member.user_id}` : `invitation:${invitation?.id ?? key}`,
      profileId: null,
      user: null,
      name: email !== '' ? email : 'Invite code',
      email,
      title: '',
      teamName: '',
      role,
      isActive: member ? member.status === 'active' : true,
      isMe: false,
      newerApp: false,
      account,
      lastSeenAt: null,
      presence: 'offline',
      storesOwned: 0,
      weekVisits: 0,
    });
  }
  return rows;
}

/** iOS TeamRosterList search: name, email, title or team name contains the text (case-insensitive). */
export function searchPeople(rows: readonly PersonRow[], query: string): PersonRow[] {
  const q = query.trim().toLowerCase();
  if (q === '') return [...rows];
  return rows.filter(
    (r) =>
      r.name.toLowerCase().includes(q) ||
      r.email.toLowerCase().includes(q) ||
      r.title.toLowerCase().includes(q) ||
      r.teamName.toLowerCase().includes(q),
  );
}

// MARK: - Sorting

export const MEMBER_SORT_KEYS = ['name', 'title', 'team', 'role', 'account', 'lastSeen', 'stores', 'visits'] as const;
export type MemberSortKey = (typeof MEMBER_SORT_KEYS)[number];

export interface MemberSort {
  key: MemberSortKey;
  desc: boolean;
}

export const DEFAULT_MEMBER_SORT: MemberSort = { key: 'name', desc: false };

const ROLE_ORDER: Readonly<Record<Role, number>> = { admin: 0, manager: 1, rep: 2 };

function compareText(a: string, b: string): number {
  // Empty values (no title, no team) sort after the filled ones whichever the direction.
  if (a === '' || b === '') return a === b ? 0 : a === '' ? 1 : -1;
  return compareLocalized(a, b);
}

/** Comparator for one column; ties fall back to the name (then id, so the order is stable). */
export function memberComparator(sort: MemberSort): (a: PersonRow, b: PersonRow) => number {
  const dir = sort.desc ? -1 : 1;
  const byName = (a: PersonRow, b: PersonRow) => compareLocalized(a.name, b.name) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  const primary = (a: PersonRow, b: PersonRow): number => {
    switch (sort.key) {
      case 'name':
        return 0;
      case 'title':
        return compareText(a.title, b.title) * (a.title === '' || b.title === '' ? 1 : dir);
      case 'team':
        return compareText(a.teamName, b.teamName) * (a.teamName === '' || b.teamName === '' ? 1 : dir);
      case 'role':
        return (ROLE_ORDER[a.role] - ROLE_ORDER[b.role]) * dir;
      case 'account': {
        const x = a.account ? ACCOUNT_ORDER[a.account.status] : 9;
        const y = b.account ? ACCOUNT_ORDER[b.account.status] : 9;
        return (x - y) * dir;
      }
      case 'lastSeen': {
        // Ascending = seen longest ago first. People never seen sort last in both directions.
        const x = a.lastSeenAt?.getTime();
        const y = b.lastSeenAt?.getTime();
        if (x === undefined || y === undefined) return x === y ? 0 : x === undefined ? 1 : -1;
        return (x - y) * dir;
      }
      case 'stores':
        return (a.storesOwned - b.storesOwned) * dir;
      case 'visits':
        return (a.weekVisits - b.weekVisits) * dir;
    }
  };
  return (a, b) => primary(a, b) || (sort.key === 'name' ? byName(a, b) * dir : byName(a, b));
}

// MARK: - Grouping

export const NO_TEAM_TITLE = 'No team';
export const INACTIVE_TITLE = 'Inactive';

/**
 * Table rows: with `grouped`, a heading per team in `teamRoster` order (teams by name, "No team" last), each followed
 * by its people in `sort` order, then an "Inactive" group (iOS leaves inactive people out of the roster; the console
 * keeps them so a suspended account can still be found). Without `grouped`, everyone in `sort` order.
 * `usersById` names each team's manager even when that manager is not visible.
 */
export function groupMemberRows(
  rows: readonly PersonRow[],
  teams: readonly TeamPayload[],
  usersById: ReadonlyMap<string, UserPayload>,
  options: { grouped: boolean; sort: MemberSort },
): MemberTableRow[] {
  const compare = memberComparator(options.sort);
  if (!options.grouped) return [...rows].sort(compare);

  const active = rows.filter((r) => r.isActive);
  const inactive = rows.filter((r) => !r.isActive);
  const activeUsers = active.flatMap((r) => (r.user ? [r.user] : []));
  const rowById = new Map(active.filter((r) => r.user).map((r) => [idKey(r.user!.id), r]));
  const out: MemberTableRow[] = [];
  let noTeam: PersonRow[] = [];
  for (const group of teamRoster(activeUsers, teams)) {
    const people = group.users.map((u) => rowById.get(idKey(u.id))).filter((r): r is PersonRow => r !== undefined);
    if (!group.team) {
      noTeam = people;
      continue;
    }
    const manager = group.team.managerId ? lookup(usersById, group.team.managerId) : undefined;
    const managerName = manager ? fullName(manager) : '';
    out.push({
      kind: 'group',
      id: `group:${idKey(group.team.id)}`,
      title: group.team.name,
      color: group.team.colorHex,
      ...(managerName !== '' ? { managerName } : {}),
      count: people.length,
    });
    out.push(...people.sort(compare));
  }
  // Rows without a profile item join "No team".
  noTeam = [...noTeam, ...active.filter((r) => !r.user)];
  if (noTeam.length > 0) {
    out.push({ kind: 'group', id: 'group:none', title: NO_TEAM_TITLE, count: noTeam.length });
    out.push(...noTeam.sort(compare));
  }
  if (inactive.length > 0) {
    out.push({ kind: 'group', id: 'group:inactive', title: INACTIVE_TITLE, count: inactive.length });
    out.push(...inactive.sort(compare));
  }
  return out;
}

// MARK: - Summary strip

export interface TeamSummary {
  /** Active people with a profile. */
  people: number;
  online: number;
  visitsToday: number;
  contactsToday: number;
  /** How many of `people` have an active account; null while the roster is unknown. */
  signedUp: number | null;
  pendingInvitations: number | null;
  expiredInvitations: number | null;
}

/**
 * The KPI strip (the iOS roster's "Reps · N online", "Visits today", "Contacts", plus the account counts) over the
 * whole visible team, whatever the search. Every count is over the same active people, so "Signed up N of M" adds up;
 * invitations are the company's open ones, as Team members lists them.
 */
export function teamSummary(
  rows: readonly PersonRow[],
  stats: Pick<MemberStats, 'todayVisits' | 'todayContacts'>,
  roster: Roster | null,
  now: Date,
): TeamSummary {
  const people = rows.filter((r) => r.user && r.isActive);
  let pending: number | null = null;
  let expired: number | null = null;
  if (roster) {
    pending = 0;
    expired = 0;
    for (const inv of roster.invitations) {
      if (isInvitationExpired(inv, now)) expired += 1;
      else pending += 1;
    }
  }
  return {
    people: people.length,
    online: people.filter((r) => r.presence === 'online').length,
    visitsToday: people.reduce((n, r) => n + count(stats.todayVisits, r.profileId), 0),
    contactsToday: people.reduce((n, r) => n + count(stats.todayContacts, r.profileId), 0),
    signedUp: roster ? people.filter((r) => r.account?.status === 'signedUp').length : null,
    pendingInvitations: pending,
    expiredInvitations: expired,
  };
}

// MARK: - Row menu

/** What a row's "…" menu offers, in menu order (TeamMembersView's context menus, plus Invite… / Invite again…). */
export type MemberAction = 'open' | 'showCode' | 'copyCode' | 'inviteAgain' | 'revoke' | 'invite' | 'remove';

/** The server rules behind the menu (session/permissions.ts; all admin-only today). */
export interface MemberActionRights {
  canInvite: boolean;
  canSeeInviteCodes: boolean;
  canRevokeInvitations: boolean;
  canRemoveMembers: boolean;
}

/**
 * The row's menu entries. Nothing account-related until the roster is known. Invite… / Invite again… only for an
 * active profile that is not the viewer's and not from a newer app (inviting patches its role and email); Remove
 * never on oneself.
 */
export function memberActions(row: PersonRow, rights: MemberActionRights): MemberAction[] {
  const out: MemberAction[] = [];
  if (row.profileId) out.push('open');
  const account = row.account;
  if (!account) return out;
  const invitable = rights.canInvite && row.profileId !== null && row.isActive && !row.isMe && !row.newerApp;
  if (account.invitation) {
    if (account.status === 'invited' && rights.canSeeInviteCodes) {
      out.push('showCode');
      if (account.invitation.code) out.push('copyCode');
    }
    if (account.status === 'expired' && invitable) out.push('inviteAgain');
    if (rights.canRevokeInvitations) out.push('revoke');
  } else if (account.status === 'none' && invitable) {
    out.push('invite');
  }
  if (account.member && !row.isMe && rights.canRemoveMembers) out.push('remove');
  return out;
}

/** Profile ids that already have an account (excluded from the invite picker, as `existingProfileIds` on iOS). */
export function memberProfileIds(roster: Roster | null): Set<string> {
  return new Set((roster?.members ?? []).map((m) => idKey(m.profile_id)));
}
